import { Divider, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { Vehicle } from 'further-types/lp/vehicle';
import { lpCompany as rules } from 'further-ui/validations';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import CurrencySelector from 'further-ui/components/CurrencySelector';
import CountrySelector from 'further-ui/components/CountrySelector';
import { Column } from 'further-ui/components/Layout';

type CoreCompanyDetailsProps = {
  selectedVehicle: Vehicle;
};

function CoreCompanyDetails({ selectedVehicle }: CoreCompanyDetailsProps) {
  const { control } = useFormContext<rules.CompanySchemaFormValues>();

  return (
    <Column spacing="lg">
      <FormField label="Vehicle">
        <Controller
          name="vehicleId"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <BasicSelect
              placeholder="Select vehicle"
              fullWidth
              disabled
              options={[
                {
                  value: selectedVehicle._id,
                  label: selectedVehicle.vehicleName,
                },
              ]}
              {...rest}
            />
          )}
        />
      </FormField>
      <Divider />
      <FormField label="Trading name">
        <Controller
          name="tradingName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter trading name, e.g. Acme"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Full legal name">
        <Controller
          name="fullLegalName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter legal name (e.g. Acme Software Limited)"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <Divider />
      <FormField label="Base currency">
        <Controller
          name="baseCurrency"
          defaultValue={selectedVehicle.baseCurrency}
          control={control}
          render={({ field: { ref, ...rest }, fieldState: { error } }) => (
            <CurrencySelector
              {...rest}
              fullWidth
              placeholder="Select base currency"
              error={error ? { message: error.message } : undefined}
            />
          )}
        />
      </FormField>
      <FormField
        label="Fully diluted capital"
        tooltip="Enter the company's current fully diluted share capital. This is used in investor reporting to show the percentage shareholding that the fund has in this company. You can change this figure at any time."
      >
        <Controller
          name="fullyDilutedCapital"
          control={control}
          render={({
            field: { onChange, ...field },
            fieldState: { error },
          }) => (
            <TextField
              {...field}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') return onChange(undefined);
                const num = Number(raw);
                if (Number.isFinite(num)) onChange(num);
              }}
              type="number"
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter number of units"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <Divider />
      <FormField label="Registration country">
        <Controller
          name="registrationCountry"
          control={control}
          defaultValue=""
          render={({
            field: { ref, value, ...rest },
            fieldState: { error },
          }) => (
            <CountrySelector
              {...rest}
              ref={ref}
              fullWidth
              value={value ?? ''}
              disableAutoFill
              mode="code"
              name="registrationCountry"
              placeholder="Select registration country"
              error={error}
              onChange={(_, option) => {
                rest.onChange(option?.code || '');
              }}
            />
          )}
        />
      </FormField>
      <FormField label="Registered number">
        <Controller
          name="registeredNumber"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter registered number"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <Divider />
      <FormField label="Registered address line 1">
        <Controller
          name="registeredAddressLine1"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              error={!!error}
              helperText={error?.message}
              placeholder="Address line 1"
            />
          )}
        />
      </FormField>
      <FormField label="Registered address line 2">
        <Controller
          name="registeredAddressLine2"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              error={!!error}
              helperText={error?.message}
              placeholder="Address line 2"
            />
          )}
        />
      </FormField>
      <FormField label="Registered address town/city">
        <Controller
          name="registeredAddressTownCity"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              error={!!error}
              helperText={error?.message}
              placeholder="Town/city"
            />
          )}
        />
      </FormField>
      <FormField label="Registered address postal code">
        <Controller
          name="registeredAddressPostalCode"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              error={!!error}
              helperText={error?.message}
              placeholder="Postal code"
            />
          )}
        />
      </FormField>
      <FormField label="Address state/region/province">
        <Controller
          name="registeredAddressState"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              error={!!error}
              helperText={error?.message}
              placeholder="State/region/province"
            />
          )}
        />
      </FormField>
      <FormField label="Registered address country">
        <Controller
          name="registeredAddressCountry"
          control={control}
          defaultValue=""
          render={({
            field: { ref, value, ...rest },
            fieldState: { error },
          }) => (
            <CountrySelector
              {...rest}
              ref={ref}
              fullWidth
              value={value || ''}
              name="registeredAddressCountry"
              placeholder="Select country"
              disableAutoFill
              mode="code"
              error={error}
              onChange={(_, option) => {
                rest.onChange(option?.code || '');
              }}
            />
          )}
        />
      </FormField>
    </Column>
  );
}

export default CoreCompanyDetails;
